import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import {
  ACTION_ITEM_KINDS,
  ACTION_ITEM_STATUSES,
  TRIAGE_CATEGORIES,
  TRIAGE_PRIORITIES,
} from "@app/types/mailbox";
import { z } from "zod";

export const INBOX_SIGNALS_SERVER_NAME = "inbox_signals" as const;
export const INBOX_SIGNALS_GET_MARKER_TOOL_NAME = "get_inbox_marker";
export const INBOX_SIGNALS_ADVANCE_MARKER_TOOL_NAME = "advance_inbox_marker";
export const INBOX_LIST_TOOL_NAME = "list_inbox";
export const INBOX_LIST_OPEN_LOOPS_TOOL_NAME = "list_open_loops";
export const INBOX_TRACK_OPEN_LOOP_TOOL_NAME = "track_open_loop";
export const INBOX_UPDATE_OPEN_LOOP_TOOL_NAME = "update_open_loop";
export const INBOX_SET_THREAD_CATEGORY_TOOL_NAME = "set_thread_category";
export const INBOX_ADD_RULE_TOOL_NAME = "add_triage_rule";
export const INBOX_LIST_RULES_TOOL_NAME = "list_triage_rules";
export const INBOX_REMOVE_RULE_TOOL_NAME = "remove_triage_rule";
export const INBOX_SYNC_TOOL_NAME = "sync_inbox";

export const INBOX_SIGNALS_TOOLS_METADATA = [
  {
    name: INBOX_SIGNALS_GET_MARKER_TOOL_NAME,
    description:
      "Get the Gmail query covering everything the inbox watch has not read yet. Call this before scanning mail, then pass the returned gmailQuery to get_messages as q, character for character. Do not compute your own after: cutoff and do not narrow the query: any cutoff later than this one silently skips mail the user is waiting on.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Checking inbox marker",
      done: "Checked inbox marker",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_SIGNALS_ADVANCE_MARKER_TOOL_NAME,
    description:
      "Move the inbox marker forward to the timestamp of the newest message handled in this run. Call this once, at the end of a scan, and never before the mail it covers has been turned into tasks or drafts.",
    schema: {
      toSeconds: z
        .number()
        .describe(
          "Unix timestamp in seconds of the newest message this run handled."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Advancing inbox marker",
      done: "Advanced inbox marker",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_LIST_TOOL_NAME,
    description:
      "List the triaged inbox: every recent thread with its category (needs_reply, action, scheduling, fyi, receipt, notification, security, newsletter, promotional, cold_outreach), whether it needs the user's reply, a one-line summary, its open loops, and the ids needed to read or draft on it. This is the sorted view every inbox routine shares — use it instead of searching Gmail to find what needs attention. Read a thread's full text with the mail provider's own tools.",
    schema: {
      categories: z
        .array(z.enum(TRIAGE_CATEGORIES))
        .optional()
        .describe("Only these categories. Omit for all."),
      needsReply: z
        .boolean()
        .optional()
        .describe(
          "Only threads that do (true) or do not (false) need a reply."
        ),
      sinceHours: z
        .number()
        .optional()
        .describe("Only threads triaged in the last N hours."),
      limit: z.number().min(1).max(100).optional().describe("Default 30."),
    },
    stake: "never_ask",
    displayLabels: { running: "Reading the inbox", done: "Read the inbox" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_LIST_OPEN_LOOPS_TOOL_NAME,
    description:
      "List every open loop in the user's mail, split by who owes the next move: what the user owes (replies asked of them, promises they made) and what others owe the user (their asks the user is waiting on, promises made to the user). Oldest first, with ages, due dates and item ids. Use for follow-ups, loose ends, 'what am I waiting on', and 'what do I owe'.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Reading open loops",
      done: "Read open loops",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_TRACK_OPEN_LOOP_TOOL_NAME,
    description:
      "Track something as an open loop on a mail thread, so it stays visible until it is closed: one line of a vendor checklist still owed, a promise the user made on a call about this thread. It is never closed by a reply on its own — close it with update_open_loop when it is actually satisfied.",
    schema: {
      threadId: z.string().describe("The thread the loop belongs to."),
      kind: z
        .enum(ACTION_ITEM_KINDS)
        .describe(
          "ask_of_them: the user is waiting on someone. promise_by_me: the user owes it. ask_of_me / promise_by_them for the other directions."
        ),
      text: z
        .string()
        .describe(
          "The deliverable, standalone. Prefix checklist items with the checklist's name."
        ),
      counterpartEmail: z.string().optional(),
      dueOn: z.string().optional().describe("ISO date, when there is one."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Tracking an open loop",
      done: "Tracked an open loop",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_UPDATE_OPEN_LOOP_TOOL_NAME,
    description:
      "Close (done or dismissed) or reopen an open loop by its item id. Close a loop as soon as the evidence shows it was delivered, so it stops appearing in sweeps and briefings.",
    schema: {
      threadId: z.string(),
      itemId: z.string(),
      status: z.enum(ACTION_ITEM_STATUSES),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Updating an open loop",
      done: "Updated an open loop",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_SET_THREAD_CATEGORY_TOOL_NAME,
    description:
      "Re-sort a thread the way the user says it should be, in Memoair's triaged inbox (their Gmail labels are left untouched). With learn=true the correction becomes a rule on the sender (or their whole domain), so future mail from them is sorted that way before any model sees it. Use whenever the user corrects a category.",
    schema: {
      threadId: z.string(),
      category: z.enum(TRIAGE_CATEGORIES),
      learn: z
        .boolean()
        .describe("Turn this correction into a rule for the sender."),
      learnScope: z
        .enum(["sender", "domain"])
        .optional()
        .describe("What the rule matches. Default sender."),
    },
    // A rule persists and silently re-sorts future mail: the person, not a
    // run reading untrusted mail, decides to add one.
    stake: "low",
    displayLabels: {
      running: "Re-sorting a thread",
      done: "Re-sorted a thread",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_ADD_RULE_TOOL_NAME,
    description:
      "Add a sorting rule the user asked for, such as 'mail from canva.com is promotional' or 'anything with Invoice in the subject is a receipt'. Rules are matched before any model, on sender, domain or subject phrase, newest first.",
    schema: {
      fromEmail: z.string().optional(),
      fromDomain: z.string().optional(),
      subjectContains: z.string().optional(),
      category: z.enum(TRIAGE_CATEGORIES),
      priority: z.enum(TRIAGE_PRIORITIES).optional(),
    },
    // A rule persists and silently re-sorts future mail: the person, not a
    // run reading untrusted mail, decides to add one.
    stake: "low",
    displayLabels: { running: "Adding a mail rule", done: "Added a mail rule" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_LIST_RULES_TOOL_NAME,
    description: "List the user's mail sorting rules, newest first, with ids.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Reading mail rules",
      done: "Read mail rules",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_REMOVE_RULE_TOOL_NAME,
    description: "Remove one of the user's mail sorting rules by id.",
    schema: { ruleId: z.string() },
    // A rule persists and silently re-sorts future mail: the person, not a
    // run reading untrusted mail, decides to add one.
    stake: "low",
    displayLabels: {
      running: "Removing a mail rule",
      done: "Removed a mail rule",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INBOX_SYNC_TOOL_NAME,
    description:
      "Ask for whatever arrived in the mailbox since the last sync to be read and triaged now, instead of waiting for the next push or poll. Use when the user asks to check their inbox or when list_inbox looks stale, then read list_inbox again.",
    schema: {},
    stake: "never_ask",
    displayLabels: { running: "Syncing the inbox", done: "Synced the inbox" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const INBOX_SIGNALS_SERVER = {
  serverInfo: {
    name: INBOX_SIGNALS_SERVER_NAME,
    version: "1.0.0",
    description:
      "The user's triaged inbox: sorted threads, open loops (who owes what), and their own sorting rules — read once by the mailbox spine and shared by every inbox routine.",
    authorization: null,
    icon: "GmailLogo",
    documentationUrl: null,
  },
  tools: INBOX_SIGNALS_TOOLS_METADATA,
} as const satisfies ServerMetadata;
